import { useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { UploadCloud, FileText, FileSpreadsheet, CheckCircle2, Loader2 } from "lucide-react";

const NONE = "__none__";
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function ImportSales() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState(null); // 'csv' | 'pdf'
  const [preview, setPreview] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [aggregate, setAggregate] = useState(false);
  const [year, setYear] = useState("");
  const [map, setMap] = useState({ date_col: "", amount_col: "", desc_col: NONE, method_col: NONE });
  const [category, setCategory] = useState("Cassa");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [deduct, setDeduct] = useState(true);
  const fileRef = useRef(null);

  const commitProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/import/sales/products/commit", { from_date: pdfData.from_date, to_date: pdfData.to_date, rows: pdfData.rows, deduct });
      setResult({ ...data, kind: "products" });
      toast.success(`Importati ${data.imported} prodotti venduti${data.deductions ? ` · scaricati ${data.deductions.deducted} piatti` : ""}`);
      if (data.deductions?.low_stock?.length) toast.warning(`Sotto soglia dopo lo scarico: ${data.deductions.low_stock.join(", ")}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const reset = () => { setPreview(null); setPdfData(null); setResult(null); setMode(null); };

  const onFile = async (f) => {
    setFile(f); reset();
    if (!f) return;
    const isPdf = f.name.toLowerCase().endsWith(".pdf");
    setMode(isPdf ? "pdf" : "csv");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      if (isPdf) {
        const { data } = await api.post("/import/sales/pdf/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (!data.count) toast.error("Nessun dato rilevato nel PDF");
        setPdfData(data);
      } else {
        const { data } = await api.post("/import/sales/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPreview(data);
        setMap({
          date_col: data.suggested.date_col || data.columns[0] || "",
          amount_col: data.suggested.amount_col || "",
          desc_col: data.suggested.desc_col || NONE,
          method_col: data.suggested.method_col || NONE,
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); reset(); }
    finally { setLoading(false); }
  };

  const commitCsv = async () => {
    if (!map.date_col || !map.amount_col) return toast.error("Seleziona le colonne Data e Importo");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("date_col", map.date_col);
      fd.append("amount_col", map.amount_col);
      fd.append("desc_col", map.desc_col === NONE ? "" : map.desc_col);
      fd.append("method_col", map.method_col === NONE ? "" : map.method_col);
      fd.append("category", category || "Cassa");
      const { data } = await api.post("/import/sales/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); toast.success(`Importati ${data.imported} incassi`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const commitPdf = async () => {
    setLoading(true);
    try {
      const rows = /^\d{4}$/.test(year) ? pdfData.rows.map((r) => ({ ...r, date: year + r.date.slice(4) })) : pdfData.rows;
      const { data } = await api.post("/import/sales/pdf/commit", { rows, aggregate_daily: aggregate });
      setResult(data); toast.success(`Importati ${data.imported} incassi`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const colSelect = (key, label, allowNone) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={map[key]} onValueChange={(v) => setMap({ ...map, [key]: v })}>
        <SelectTrigger data-testid={`map-${key}`}><SelectValue placeholder="Scegli colonna" /></SelectTrigger>
        <SelectContent>
          {allowNone && <SelectItem value={NONE}>— Nessuna —</SelectItem>}
          {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <PageHeader title="Import Cassa" subtitle="Importa gli incassi da EasyEvolver — PDF, CSV o XML" testid="import-header" />

      <Card className="p-6 rounded-xl mb-6" data-testid="import-upload-card">
        <input ref={fileRef} type="file" accept=".pdf,.csv,.xml,application/pdf,text/csv,text/xml,application/xml" className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
        <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-border rounded-xl text-center cursor-pointer hover:border-primary transition-colors"
          onClick={() => fileRef.current?.click()} data-testid="import-dropzone">
          <UploadCloud className="w-10 h-10 text-primary mb-3" />
          <p className="font-medium">Carica il report esportato dalla cassa</p>
          <p className="text-sm text-muted-foreground mt-1">PDF (report venduto) · CSV / XML (ExportVenduto)</p>
          {file && <p className="text-sm mt-3 flex items-center gap-2">{mode === "pdf" ? <FileText className="w-4 h-4 text-primary" /> : <FileSpreadsheet className="w-4 h-4 text-primary" />}{file.name}</p>}
        </div>
      </Card>

      {loading && !preview && !pdfData && (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> {mode === "pdf" ? "Lettura del PDF…" : "Analisi del file…"}</div>
      )}

      {result && result.kind === "products" && (
        <Card className="p-5 rounded-xl mb-6 border-[hsl(var(--success))]/40" data-testid="import-result">
          <div className="flex items-center gap-2 text-[hsl(var(--success))] font-medium mb-2"><CheckCircle2 className="w-5 h-5" /> Venduto importato</div>
          <p className="text-sm">Prodotti importati: <b>{result.imported}</b> · Già presenti (saltati): {result.skipped}
            {result.deductions && <> · Scaricati dal magazzino: <b>{result.deductions.deducted}</b> piatti · Senza ricetta: {result.deductions.unmatched}</>}</p>
          {result.deductions?.missing_ingredients?.length > 0 && <p className="text-xs text-amber-600 mt-1">Ingredienti non trovati in magazzino: {result.deductions.missing_ingredients.join(", ")}</p>}
          <p className="text-xs text-muted-foreground mt-2">Abbina i piatti senza ricetta nella sezione <b>Vendite</b>. Il food cost reale è in <b>Analisi</b>.</p>
        </Card>
      )}

      {/* PDF Venduto Prodotti */}
      {mode === "pdf" && pdfData?.mode === "products" && (
        <>
          <Card className="p-6 rounded-xl mb-6" data-testid="products-summary-card">
            <h3 className="font-dash font-semibold text-lg mb-1">Venduto Prodotti · dal {pdfData.from_date} al {pdfData.to_date}</h3>
            <div className="flex flex-wrap items-center gap-6 mt-3">
              <div><p className="text-xs uppercase text-muted-foreground">Prodotti</p><p className="font-dash text-2xl font-semibold">{pdfData.count}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground">Pezzi</p><p className="font-dash text-2xl font-semibold">{pdfData.total_qty}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground">Totale venduto</p><p className="font-dash text-2xl font-semibold text-primary">{eur(pdfData.total)}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground">Reparti</p><p className="font-dash text-2xl font-semibold">{pdfData.departments?.length}</p></div>
              <div className="flex items-center gap-3 ml-auto">
                <Switch checked={deduct} onCheckedChange={setDeduct} data-testid="deduct-switch" />
                <Label className="cursor-pointer">Scarica ingredienti dal magazzino (ricette Food Cost)</Label>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Button onClick={commitProducts} disabled={loading || !pdfData.count} className="rounded-full" data-testid="products-commit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importa venduto per prodotto"}
              </Button>
              <span className="text-xs text-muted-foreground">Registra il venduto per piatto (non crea incassi: quelli arrivano dal Rapporto o dall'ingest giornaliero).</span>
            </div>
          </Card>
          <Card className="rounded-xl overflow-hidden" data-testid="products-preview-card">
            <div className="p-3 border-b border-border text-sm font-medium">Anteprima ({Math.min(pdfData.rows.length, 40)} di {pdfData.count})</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Reparto</TableHead><TableHead>Prodotto</TableHead><TableHead className="text-right">Q.tà</TableHead><TableHead className="text-right">Prezzo medio</TableHead><TableHead className="text-right">Totale</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pdfData.rows.slice(0, 40).map((r, i) => (
                    <TableRow key={i}><TableCell className="text-muted-foreground text-xs">{r.department}</TableCell><TableCell>{r.product}</TableCell><TableCell className="text-right">{r.qty}</TableCell><TableCell className="text-right">{eur(r.avg_price)}</TableCell><TableCell className="text-right">{eur(r.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {mode === "pdf" && pdfData?.mode === "rapporto" && (
        <Card className="p-6 rounded-xl mb-6" data-testid="rapporto-card">
          <h3 className="font-dash font-semibold text-lg mb-1">Rapporto complessivo · dal {pdfData.from_date} al {pdfData.to_date}</h3>
          <p className="text-sm text-muted-foreground mb-4">Gli incassi vengono registrati per tipo di documento: Scontrini fiscali e Fatture come <b>ufficiali</b>, Preventivi come <b>black</b>. Data di registrazione: {pdfData.from_date}.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-2">Documenti</p>
              {pdfData.rows.map((r, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-border/60"><span>{r.department} <span className="text-xs text-muted-foreground">({r.docs} doc · {r.ledger})</span></span><b>{eur(r.amount)}</b></div>)}
              <div className="flex justify-between text-sm py-1 font-semibold"><span>Totale</span><span>{eur(pdfData.total)}</span></div>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground mb-2">Pagamenti</p>
              {(pdfData.payments || []).map((p, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-border/60"><span>{p.method}</span><span>{eur(p.amount)}</span></div>)}
              <p className="text-xs uppercase text-muted-foreground mt-4 mb-2">Reparti</p>
              <div className="flex flex-wrap gap-1">{(pdfData.departments || []).map((d, i) => <span key={i} className="text-xs bg-secondary rounded-full px-2 py-0.5">{d.department} {eur(d.amount)}</span>)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Button onClick={commitPdf} disabled={loading || !pdfData.count} className="rounded-full" data-testid="rapporto-commit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importa incassi in Contabilità"}
            </Button>
            <span className="text-xs text-muted-foreground">I doppioni vengono ignorati automaticamente.</span>
          </div>
        </Card>
      )}

      {result && result.kind !== "products" && (
        <Card className="p-5 rounded-xl mb-6 border-[hsl(var(--success))]/40" data-testid="import-result">
          <div className="flex items-center gap-2 text-[hsl(var(--success))] font-medium mb-2"><CheckCircle2 className="w-5 h-5" /> Importazione completata</div>
          <p className="text-sm">Incassi importati: <b>{result.imported}</b> · Già presenti (saltati): {result.skipped}{result.invalid != null ? ` · Non validi: ${result.invalid}` : ""} · Totale: {result.total}</p>
        </Card>
      )}

      {/* PDF flow */}
      {mode === "pdf" && pdfData && pdfData.mode === "daily" && (
        <>
          <Card className="p-6 rounded-xl mb-6" data-testid="pdf-summary-card">
            <div className="flex flex-wrap items-center gap-6">
              <div><p className="text-xs uppercase text-muted-foreground">Righe rilevate</p><p className="font-dash text-2xl font-semibold">{pdfData.count}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground">Giorni</p><p className="font-dash text-2xl font-semibold">{pdfData.days.length}</p></div>
              <div><p className="text-xs uppercase text-muted-foreground">Totale incassi</p><p className="font-dash text-2xl font-semibold text-primary">{eur(pdfData.total)}</p></div>
              <div className="space-y-1">
                <p className="text-xs uppercase text-muted-foreground">Anno (se assente nel report)</p>
                <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder={String(new Date().getFullYear())} className="h-9 w-28" data-testid="pdf-year-input" />
              </div>
              <div className="flex items-center gap-3 ml-auto">
                <Switch checked={aggregate} onCheckedChange={setAggregate} data-testid="aggregate-switch" />
                <Label className="cursor-pointer">Raggruppa per giorno (un incasso totale al giorno)</Label>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Button onClick={commitPdf} disabled={loading || !pdfData.count} className="rounded-full" data-testid="pdf-commit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importa incassi in Contabilità"}
              </Button>
              <span className="text-xs text-muted-foreground">I doppioni vengono ignorati automaticamente.</span>
            </div>
          </Card>

          <Card className="rounded-xl overflow-hidden" data-testid="pdf-preview-card">
            <div className="p-3 border-b border-border text-sm font-medium">Anteprima ({Math.min(pdfData.rows.length, 30)} di {pdfData.count})</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Reparto</TableHead><TableHead className="text-right">Importo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pdfData.rows.slice(0, 30).map((r, i) => (
                    <TableRow key={i}><TableCell>{r.date}</TableCell><TableCell>{r.department}</TableCell><TableCell className="text-right">{eur(r.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {/* CSV/XML flow */}
      {mode === "csv" && preview && (
        <>
          <Card className="p-6 rounded-xl mb-6" data-testid="import-mapping-card">
            <h3 className="font-dash font-semibold text-lg mb-1">Associa le colonne</h3>
            <p className="text-sm text-muted-foreground mb-4">Rilevate {preview.columns.length} colonne · {preview.total} righe.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {colSelect("date_col", "Data *", false)}
              {colSelect("amount_col", "Importo incasso *", false)}
              {colSelect("desc_col", "Descrizione (opz)", true)}
              {colSelect("method_col", "Metodo pagamento (opz)", true)}
              <div className="space-y-2"><Label>Categoria contabile</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="import-category-input" /></div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Button onClick={commitCsv} disabled={loading} className="rounded-full" data-testid="import-commit-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importa incassi"}
              </Button>
              <span className="text-xs text-muted-foreground">I doppioni vengono ignorati automaticamente.</span>
            </div>
          </Card>

          <Card className="rounded-xl overflow-hidden" data-testid="import-preview-card">
            <div className="p-3 border-b border-border text-sm font-medium">Anteprima (prime {preview.rows.length} righe)</div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{preview.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {preview.rows.map((row, i) => (
                    <TableRow key={i}>{preview.columns.map((c) => <TableCell key={c}>{row[c]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
